import React, { useEffect, useMemo, useRef, useState } from "react";
import { Upload, FileSpreadsheet, Loader2, X, CheckCircle2, AlertCircle } from "lucide-react";
import { Button } from "@/components/ui/button";
import api, { formatApiError } from "@/lib/api";
import { toast } from "@/components/ui/sonner";

/** Minimal RFC-4180 CSV parser — handles quoted fields with embedded commas.
 *  Returns an array of objects keyed by lower-cased header. */
function parseCsv(text) {
    const rows = [];
    let field = "";
    let row = [];
    let inQuotes = false;
    for (let i = 0; i < text.length; i++) {
        const ch = text[i];
        if (inQuotes) {
            if (ch === '"') {
                if (text[i + 1] === '"') { field += '"'; i++; }
                else { inQuotes = false; }
            } else { field += ch; }
        } else {
            if (ch === '"') { inQuotes = true; }
            else if (ch === ",") { row.push(field); field = ""; }
            else if (ch === "\n" || ch === "\r") {
                if (ch === "\r" && text[i + 1] === "\n") i++;
                row.push(field); rows.push(row); row = []; field = "";
            } else { field += ch; }
        }
    }
    if (field.length || row.length) { row.push(field); rows.push(row); }
    if (rows.length === 0) return { headers: [], objects: [] };
    const headers = rows[0].map((h) => h.trim().toLowerCase());
    const objects = rows.slice(1)
        .filter((r) => r.some((c) => (c || "").trim() !== ""))
        .map((r) => {
            const obj = {};
            headers.forEach((h, idx) => { obj[h] = (r[idx] || "").trim(); });
            return obj;
        });
    return { headers, objects };
}

const REQUIRED_HEADER = "email";
const OPTIONAL_HEADERS = ["name", "linkedin_url", "title", "location"];

export default function BulkInviteModal({ searchId, onClose, onDone }) {
    const [csvText, setCsvText] = useState("");
    const [result, setResult] = useState(null);       // final job snapshot when done
    const [dryRun, setDryRun] = useState(null);       // preview split
    const [phase, setPhase] = useState("input");     // input | preview | sending | done
    const [job, setJob] = useState(null);            // live job snapshot while sending
    const pollTimerRef = useRef(null);
    const mountedRef = useRef(true);

    const parsed = useMemo(() => {
        if (!csvText.trim()) return { headers: [], objects: [] };
        return parseCsv(csvText);
    }, [csvText]);

    const missingEmail = parsed.headers.length && !parsed.headers.includes(REQUIRED_HEADER);
    const rows = parsed.objects.map((o) => ({
        email: o.email || "",
        name: o.name || null,
        linkedin_url: o.linkedin_url || null,
        title: o.title || null,
        location: o.location || null,
    }));

    const handleFile = async (file) => {
        if (!file) return;
        if (file.size > 512 * 1024) { toast.error("CSV must be ≤ 512 KB."); return; }
        const text = await file.text();
        setCsvText(text);
    };

    const runPreview = async () => {
        if (!rows.length) return;
        if (rows.length > 500) { toast.error("Max 500 rows per batch — split the CSV."); return; }
        setPhase("preview");
        try {
            const { data } = await api.post(`/admin/recruit/searches/${searchId}/bulk-invite`,
                { rows, dry_run: true });
            setDryRun(data);
        } catch (err) { toast.error(formatApiError(err)); setPhase("input"); }
    };

    const runSend = async () => {
        if (!rows.length) return;
        setPhase("sending");
        try {
            const { data } = await api.post(`/admin/recruit/searches/${searchId}/bulk-invite`,
                { rows, dry_run: false });
            // Async pipeline: response returns immediately with a job_id.
            // Poll the snapshot endpoint every 800ms until status is done/failed.
            const jobId = data.job_id;
            setJob({ ...data, processed: 0, sent: 0, failed: 0,
                     skipped_duplicate: 0, skipped_invalid: 0 });
            const startedAt = Date.now();
            const MAX_POLL_MS = 16 * 60 * 1000; // 16m — slightly longer than the reaper's 15m
            const poll = async () => {
                if (!mountedRef.current) return;
                if (Date.now() - startedAt > MAX_POLL_MS) {
                    // Safety net — reaper should have already flipped the job to
                    // 'failed', but guard against the unlikely case the sweeper
                    // is disabled or lagging.
                    toast.error("This batch is taking too long to report progress. Refresh the page to check its final status.");
                    setPhase("preview");
                    return;
                }
                try {
                    const { data: snap } = await api.get(`/admin/recruit/bulk-jobs/${jobId}`);
                    // Race guard: an in-flight request may resolve AFTER the
                    // modal unmounts — never touch state / schedule a new poll.
                    if (!mountedRef.current) return;
                    setJob(snap);
                    if (snap.status === "done" || snap.status === "failed") {
                        setResult(snap);
                        setPhase("done");
                        if (snap.status === "done") {
                            toast.success(`Sent ${snap.sent} invitation${snap.sent === 1 ? "" : "s"}.`);
                        } else {
                            toast.error(`Bulk job failed: ${snap.error || "unknown"}`);
                        }
                        return;
                    }
                    pollTimerRef.current = setTimeout(poll, 800);
                } catch (err) {
                    if (!mountedRef.current) return;
                    toast.error(formatApiError(err));
                    setPhase("preview");
                }
            };
            pollTimerRef.current = setTimeout(poll, 400);
        } catch (err) { toast.error(formatApiError(err)); setPhase("preview"); }
    };

    // Clean up the polling timer on unmount so a closed modal stops hitting the API.
    useEffect(() => {
        mountedRef.current = true;
        return () => {
            mountedRef.current = false;
            if (pollTimerRef.current) clearTimeout(pollTimerRef.current);
        };
    }, []);

    return (
        <div
            className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4"
            onClick={onClose}
            data-testid="bulk-invite-modal"
        >
            <div
                className="bg-background border border-border rounded-lg max-w-3xl w-full max-h-[90vh] overflow-hidden flex flex-col"
                onClick={(e) => e.stopPropagation()}
            >
                <div className="p-4 border-b border-border flex items-center justify-between">
                    <div className="flex items-center gap-2">
                        <FileSpreadsheet size={16} className="text-violet-500" />
                        <div>
                            <div className="font-medium">Bulk invite from CSV</div>
                            <div className="text-xs text-muted-foreground">
                                Required column: <code className="text-[10px] bg-muted px-1 rounded">email</code>. Optional: <code className="text-[10px] bg-muted px-1 rounded">name, linkedin_url, title, location</code>
                            </div>
                        </div>
                    </div>
                    <button onClick={onClose} className="p-1 hover:bg-muted rounded" data-testid="bulk-invite-close">
                        <X size={16} />
                    </button>
                </div>

                <div className="p-4 overflow-auto flex-1">
                    {phase === "input" && (
                        <div className="space-y-3" data-testid="bulk-invite-input">
                            <div className="flex items-center gap-2">
                                <label className="text-xs px-3 py-1.5 rounded-md border border-border hover:border-violet-500 cursor-pointer inline-flex items-center gap-1.5" data-testid="bulk-invite-upload-label">
                                    <Upload size={12} /> Upload CSV
                                    <input type="file" accept=".csv,text/csv" className="hidden" onChange={(e) => handleFile(e.target.files?.[0])} data-testid="bulk-invite-upload" />
                                </label>
                                <span className="text-xs text-muted-foreground">or paste below</span>
                            </div>
                            <textarea
                                value={csvText}
                                onChange={(e) => setCsvText(e.target.value)}
                                placeholder={"email,name,title,location\nalex@example.com,Alex Kim,Bartender,SF\nsam@example.com,Sam Lee,Barista,Oakland"}
                                rows={10}
                                className="w-full px-2.5 py-1.5 text-xs font-mono bg-background border border-border rounded-md focus:border-violet-500 focus:outline-none"
                                data-testid="bulk-invite-textarea"
                            />
                            {missingEmail && (
                                <div className="text-xs text-red-500 flex items-center gap-1.5" data-testid="bulk-invite-header-error">
                                    <AlertCircle size={12} /> CSV is missing the required <code className="bg-muted px-1 rounded">email</code> header.
                                </div>
                            )}
                            {rows.length > 0 && !missingEmail && (
                                <div className="text-xs text-muted-foreground" data-testid="bulk-invite-parsed-count">
                                    Parsed <b className="text-foreground">{rows.length}</b> row{rows.length === 1 ? "" : "s"}. Headers detected: <span className="font-mono">{parsed.headers.join(", ")}</span>
                                </div>
                            )}
                        </div>
                    )}

                    {phase === "preview" && dryRun && (
                        <div className="space-y-3" data-testid="bulk-invite-preview">
                            <div className="grid grid-cols-4 gap-2 text-center">
                                <div className="card-soft p-2"><div className="text-[10px] uppercase text-muted-foreground">Will send</div><div className="text-lg font-semibold text-green-500" data-testid="preview-sent">{dryRun.sent}</div></div>
                                <div className="card-soft p-2"><div className="text-[10px] uppercase text-muted-foreground">Duplicates</div><div className="text-lg font-semibold text-amber-500" data-testid="preview-dupes">{dryRun.skipped_duplicate}</div></div>
                                <div className="card-soft p-2"><div className="text-[10px] uppercase text-muted-foreground">Invalid</div><div className="text-lg font-semibold text-red-500" data-testid="preview-invalid">{dryRun.skipped_invalid}</div></div>
                                <div className="card-soft p-2"><div className="text-[10px] uppercase text-muted-foreground">Total rows</div><div className="text-lg font-semibold" data-testid="preview-total">{dryRun.total_rows}</div></div>
                            </div>
                            <div className="border border-border rounded-md max-h-[280px] overflow-auto">
                                <table className="w-full text-xs">
                                    <thead className="bg-muted/40 sticky top-0">
                                        <tr><th className="text-left px-2 py-1.5">Email</th><th className="text-left px-2 py-1.5">Status</th></tr>
                                    </thead>
                                    <tbody className="divide-y divide-border">
                                        {dryRun.details.map((d, i) => (
                                            <tr key={i} data-testid={`preview-row-${i}`}>
                                                <td className="px-2 py-1 font-mono">{d.email}</td>
                                                <td className="px-2 py-1">
                                                    <span className={
                                                        d.status === "would_send" ? "text-green-500" :
                                                        d.status === "duplicate" ? "text-amber-500" :
                                                        "text-red-500"
                                                    }>{d.status}</span>
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                            {dryRun.sent === 0 && (
                                <div className="text-xs text-muted-foreground" data-testid="preview-nothing">Nothing to send — every valid row is either invalid or already invited.</div>
                            )}
                        </div>
                    )}

                    {phase === "sending" && (
                        <div className="py-8 space-y-4" data-testid="bulk-invite-sending">
                            <div className="text-center">
                                <Loader2 size={22} className="animate-spin text-violet-500 mx-auto" />
                                <div className="text-sm text-muted-foreground mt-2">
                                    Sending {job?.total ?? dryRun?.sent ?? 0} invitation{(job?.total ?? 0) === 1 ? "" : "s"}
                                    {job?.status ? <span className="ml-1 text-violet-600 font-medium">· {job.status}</span> : null}
                                </div>
                            </div>
                            {/* Progress bar (data-testid: bulk-invite-progress-bar) */}
                            <div className="w-full bg-muted rounded-full h-2 overflow-hidden" data-testid="bulk-invite-progress-track">
                                <div
                                    className="bg-violet-500 h-full transition-all duration-500"
                                    style={{
                                        width: job && job.total > 0
                                            ? `${Math.min(100, Math.round((job.processed / job.total) * 100))}%`
                                            : "0%",
                                    }}
                                    data-testid="bulk-invite-progress-bar"
                                />
                            </div>
                            <div className="text-center text-xs text-muted-foreground" data-testid="bulk-invite-progress-label">
                                {job ? `${job.processed || 0} / ${job.total || 0} processed` : "Starting…"}
                            </div>
                            {/* Live counters */}
                            {job && (
                                <div className="grid grid-cols-4 gap-2 text-center">
                                    <div className="card-soft p-2">
                                        <div className="text-[10px] uppercase text-muted-foreground">Sent</div>
                                        <div className="text-lg font-semibold text-green-500" data-testid="send-live-sent">{job.sent || 0}</div>
                                    </div>
                                    <div className="card-soft p-2">
                                        <div className="text-[10px] uppercase text-muted-foreground">Duplicates</div>
                                        <div className="text-lg font-semibold text-amber-500" data-testid="send-live-dupes">{job.skipped_duplicate || 0}</div>
                                    </div>
                                    <div className="card-soft p-2">
                                        <div className="text-[10px] uppercase text-muted-foreground">Invalid</div>
                                        <div className="text-lg font-semibold text-red-500" data-testid="send-live-invalid">{job.skipped_invalid || 0}</div>
                                    </div>
                                    <div className="card-soft p-2">
                                        <div className="text-[10px] uppercase text-muted-foreground">Failed</div>
                                        <div className="text-lg font-semibold text-red-500" data-testid="send-live-failed">{job.failed || 0}</div>
                                    </div>
                                </div>
                            )}
                            <div className="text-[11px] text-muted-foreground text-center">
                                Safe to keep working — this runs in the background. You can close this modal and the batch will finish.
                            </div>
                        </div>
                    )}

                    {phase === "done" && result && (
                        <div className="space-y-3" data-testid="bulk-invite-done">
                            <div className="flex items-center gap-2 text-green-500">
                                <CheckCircle2 size={16} /> <span className="font-medium">Batch complete</span>
                            </div>
                            <div className="grid grid-cols-4 gap-2 text-center">
                                <div className="card-soft p-2"><div className="text-[10px] uppercase text-muted-foreground">Sent</div><div className="text-lg font-semibold text-green-500" data-testid="done-sent">{result.sent}</div></div>
                                <div className="card-soft p-2"><div className="text-[10px] uppercase text-muted-foreground">Duplicates</div><div className="text-lg font-semibold text-amber-500" data-testid="done-dupes">{result.skipped_duplicate}</div></div>
                                <div className="card-soft p-2"><div className="text-[10px] uppercase text-muted-foreground">Invalid</div><div className="text-lg font-semibold text-red-500" data-testid="done-invalid">{result.skipped_invalid}</div></div>
                                <div className="card-soft p-2"><div className="text-[10px] uppercase text-muted-foreground">Failed</div><div className="text-lg font-semibold text-red-500" data-testid="done-failed">{result.failed}</div></div>
                            </div>
                            {result.failed > 0 && (
                                <div className="text-xs text-red-500" data-testid="done-failed-note">
                                    {result.failed} email{result.failed === 1 ? "" : "s"} failed to send. Check backend logs / Resend dashboard.
                                </div>
                            )}
                        </div>
                    )}
                </div>

                <div className="p-4 border-t border-border flex items-center justify-end gap-2">
                    {phase === "input" && (
                        <>
                            <Button variant="ghost" size="sm" onClick={onClose} data-testid="bulk-invite-cancel">Cancel</Button>
                            <Button size="sm" onClick={runPreview} disabled={!rows.length || missingEmail} data-testid="bulk-invite-preview-btn">
                                Preview {rows.length > 0 ? `(${rows.length})` : ""}
                            </Button>
                        </>
                    )}
                    {phase === "preview" && dryRun && (
                        <>
                            <Button variant="ghost" size="sm" onClick={() => setPhase("input")} data-testid="bulk-invite-back">Back</Button>
                            <Button size="sm" onClick={runSend} disabled={dryRun.sent === 0} data-testid="bulk-invite-send-btn">
                                Send {dryRun.sent} invitation{dryRun.sent === 1 ? "" : "s"}
                            </Button>
                        </>
                    )}
                    {phase === "done" && (
                        <Button size="sm" onClick={() => { onDone?.(result); onClose(); }} data-testid="bulk-invite-close-done">Done</Button>
                    )}
                </div>
            </div>
        </div>
    );
}
